import React, { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { Section } from "@/components/Section";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_SM } from "@/core/media";
import { Flex, IDialogModal, inDialogModal } from "@/eworkitComponents";
import { CloseButton } from "@/views/components/CloseButton";
import { StyledModal } from "@/views/components/Modal";

interface IContextSwitchDialogProps extends IDialogModal {
  onAccept: () => void;
  onCancel?: () => void;
  acceptButtonLabel: string;
  cancelButtonLabel: string;
  fromContext: ReactElement | null;
  toContext: ReactElement | null;
}

const ContextSwitchDialog: FC<IContextSwitchDialogProps> = ({
  onClose,
  acceptButtonLabel,
  cancelButtonLabel,
  onAccept,
  onCancel,
  fromContext,
  toContext,
}) => {
  const { t } = useTranslation("session");
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const handleCancel = () => {
    onClose();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <StyledModal>
      <Flex direction="column" padding={["lg"]} innerMargin="lg">
        <Flex justify="space-between">
          <Header weight="bold">{t("USERS.CONTEXT_SWITCHING.HEADER")}</Header>
          <CloseButton onClick={handleCancel} testName="close" />
        </Flex>
        <Flex direction="column" innerMargin="lg">
          <Flex direction={isSmallScreen ? "column" : "row"} innerMargin="md">
            <Flex direction="column" justify="flex-end">
              <Text color="grey3">{t("USERS.CONTEXT_SWITCHING.FROM")}</Text>
              <Section border={[true]} borderRadius shadow="none">
                <Flex align="center" justify="center" padding={["lg"]}>
                  {fromContext}
                </Flex>
              </Section>
            </Flex>
            {!isSmallScreen && (
              <Flex direction="column" align="center" justify="center">
                <Icon icon="ic_arrow_forward" color="grey3" size="xl" />
              </Flex>
            )}
            <Flex direction="column">
              <Text color="grey3">{t("USERS.CONTEXT_SWITCHING.TO")}</Text>
              <Section border={[true]} borderRadius shadow="none">
                <Flex align="center" justify="center" padding={["lg"]}>
                  {toContext}
                </Flex>
              </Section>
            </Flex>
          </Flex>
          <Flex justify="flex-end" innerMargin="md">
            <Button variant="secondary" onClick={handleCancel} size="lg" testName="cancel">
              <Text>{cancelButtonLabel}</Text>
            </Button>
            <Button onClick={onAccept} size="lg" testName="delete">
              <Text>{acceptButtonLabel}</Text>
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </StyledModal>
  );
};

const ContextSwitchDialogModalComponent = inDialogModal(ContextSwitchDialog);

interface IContextSwitchModalProps extends IContextSwitchDialogProps {}

export const ContextSwitchModal: FC<IContextSwitchModalProps> = props => {
  return <ContextSwitchDialogModalComponent size="xs" {...props} />;
};
